import { useCallback, useRef } from 'react'

import type { PostStatus } from '~publish/components/graphqlEnums'
import { useChannels } from '~publish/components/PublishSidebar/useChannels'
import { graphql, readFragment } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { debugLog } from '~publish/helpers/debugLog'
import { isPostSatisfyingFilters } from '~publish/helpers/posts/isPostSatisfyingFilters'
import { reconcilePostScheduledTimeEcho } from '~publish/helpers/posts/recentPostScheduledTimeChanges'
import { isHeldAgainstTagFilterOnly } from '~publish/helpers/posts/tagEditVisibility'
import { client } from '~publish/legacy/apollo-client'
import { RealTimeUpdates_Post } from '~publish/pages/AllChannels/PostList/useRealTimePusherUpdates'
import { CalendarPostCard_Post } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

export const GetCalendarPostForRealTimeUpdate = graphql(
  `
    query GetCalendarPostForRealTimeUpdate($postId: PostId!) {
      post(input: { id: $postId }) {
        id
        dueAt
        ...CalendarPostCard_Post
        ...RealTimeUpdates_Post
      }
    }
  `,
  [CalendarPostCard_Post, RealTimeUpdates_Post],
)

type GetCalendarPostForRealTimeUpdateData = ResultOf<
  typeof GetCalendarPostForRealTimeUpdate
>

const CalendarRealTimeUpdatesScheduledTimePost = graphql(`
  fragment CalendarRealTimeUpdatesScheduledTimePost on Post {
    id
    dueAt
  }
`)

type PusherEventData = {
  update_id: string
  draft_id: string
  profile_id?: string
}

const postEvents = [
  PusherEvent.POST_UPDATED,
  PusherEvent.POST_TAG_ADDED,
  PusherEvent.POST_TAG_REMOVED,
  PusherEvent.POST_NOTE_ADDED,
  PusherEvent.POST_NOTE_DELETED,
  PusherEvent.POST_NOTE_UPDATED,
  PusherEvent.DRAFT_UPDATED,
  PusherEvent.POST_SENT,
  PusherEvent.DRAFT_MOVED,
  PusherEvent.DRAFT_APPROVED,
  PusherEvent.POST_CREATED,
]

type CalendarPostsFilters = {
  channelIds?: string[] | null
  tagIds?: string[] | null
  status?: PostStatus[] | null
}

type UseCalendarRealTimeUpdatesOptions = {
  filters: CalendarPostsFilters
  refetch: () => Promise<void>
  isPostVisible: (id: string) => boolean
  dateRange: {
    startDate: string
    endDate: string
  }
}

/**
 * Hook that handles real-time updates to posts in the calendar view via Pusher events
 * Manages post visibility, ordering, and cache updates based on various events
 */
export const useCalendarRealTimeUpdates = ({
  filters,
  isPostVisible,
  refetch,
  dateRange,
}: UseCalendarRealTimeUpdatesOptions): void => {
  const { channels } = useChannels()
  const isPostVisibleRef = useRef(isPostVisible)
  isPostVisibleRef.current = isPostVisible

  const getPost = useCallback(
    (options: { variables: { postId: string } }) =>
      client.query({
        query: GetCalendarPostForRealTimeUpdate,
        variables: options.variables,
        fetchPolicy: 'no-cache',
      }),
    [],
  )

  /**
   * Updates a post's data and manages its visibility in the calendar
   */
  const updatePost = async (id: string): Promise<void> => {
    const { data } = await getPost({
      variables: {
        postId: id,
      },
    })

    const updatedPost = data?.post
    if (!updatedPost) {
      return
    }

    // Read cache state after the network request. A local drag can update the
    // post and its connection membership while this request is in flight.
    const postCacheId = client.cache.identify({
      __typename: 'Post',
      id,
    })
    const persistedPost = client.cache.readFragment({
      id: postCacheId,
      fragment: CalendarRealTimeUpdatesScheduledTimePost,
    })
    const visiblePost = client.cache.readFragment({
      optimistic: true,
      id: postCacheId,
      fragment: CalendarRealTimeUpdatesScheduledTimePost,
    })
    const wasPostVisibleBeforeUpdate = isPostVisibleRef.current(id)

    const {
      dueAt,
      dueAtForCache,
      isStale: isStaleScheduledTimeEcho,
    } = reconcilePostScheduledTimeEcho(
      id,
      updatedPost.dueAt,
      persistedPost,
      visiblePost,
    )
    const postForUpdate = isStaleScheduledTimeEcho
      ? { ...updatedPost, dueAt }
      : updatedPost
    const postForCache =
      dueAtForCache !== updatedPost.dueAt
        ? { ...updatedPost, dueAt: dueAtForCache }
        : updatedPost
    const dataForCache =
      postForCache !== updatedPost ? { ...data, post: postForCache } : data

    const { satisfies, filterViolations } = isPostSatisfyingFilters(
      readFragment(RealTimeUpdates_Post, postForUpdate),
      filters,
    )

    // While a post's tag menu is open we pin it visible against a *tag-filter*
    // mismatch only, so several tags can be added without the row vanishing.
    // Every other change (sent, rescheduled out of the date range, moved to
    // another channel) still flows through the normal handling below so an
    // edited row can't get stuck. The deferred tag removal runs on menu close
    // (see PostCardTags / removePostFromMismatchedTagLists).
    const shouldUpdatedPostBeVisible =
      satisfies || isHeldAgainstTagFilterOnly(id, filterViolations)
    const isWithinDateRange =
      dueAt != null &&
      dueAt >= dateRange.startDate &&
      dueAt <= dateRange.endDate

    // Handle post that moves outside the calendar date range
    if (!isWithinDateRange) {
      if (wasPostVisibleBeforeUpdate) {
        await removePostFromCache(id)
      }
      return
    }

    // Handle post that should be removed due to filters
    if (wasPostVisibleBeforeUpdate && !shouldUpdatedPostBeVisible) {
      await removePostFromCache(id)
      return
    }

    // Handle post that should now be visible
    if (
      !wasPostVisibleBeforeUpdate &&
      shouldUpdatedPostBeVisible &&
      isWithinDateRange
    ) {
      await refetch()
      return
    }

    // Update post data for all other cases
    updateCachedPost(id, dataForCache)
  }

  /**
   * Checks if a channel exists on the fetched channels list
   * @param profileId - The ID of the profile
   * @returns True if the channel exists, false otherwise
   */
  // TODO: this is a temporary solution, because pusher events are sent to all
  // users of the organization regardless of which channels they have access to
  // we should update pusher events to be more specific to the user
  const isChannelAllowed = (profileId?: string): boolean => {
    return (
      profileId !== undefined &&
      channels.find((c) => c.id === profileId) !== undefined
    )
  }

  usePusherEvent(
    PusherEvent.POSTS_REORDERED,
    async (data?: {
      profile_id?: string
      update_ids?: Array<string>
    }): Promise<void> => {
      debugLog('💽 POSTS_REORDERED', data)
      const hasChanges = Number(data?.update_ids?.length) > 0
      const isProfileIncluded =
        // no filters means all channles are included
        filters?.channelIds?.length === 0 ||
        filters?.channelIds?.includes(data?.profile_id ?? '')
      if (!hasChanges || !isProfileIncluded) {
        return
      }

      if (!isChannelAllowed(data?.profile_id)) {
        return
      }

      if (data?.update_ids?.length === 1) {
        updatePost(data.update_ids[0])
      } else {
        refetch()
      }
    },
  )

  usePusherEvent(
    PusherEvent.POST_DELETED,
    async (data: PusherEventData): Promise<void> => {
      debugLog('💽 POST_DELETED', data)
      await removePostFromCache(data.update_id)
    },
  )

  usePusherEvent(postEvents, (data: PusherEventData): void => {
    debugLog('💽 CALENDAR_POST_EVENTS', data)
    if (!isChannelAllowed(data?.profile_id)) {
      return
    }

    updatePost(data.update_id ?? data.draft_id)
  })
}

/**
 * Removes a post from the Apollo cache
 */
async function removePostFromCache(id: string): Promise<void> {
  const normalizedId = client.cache.identify({
    id,
    __typename: 'Post',
  })

  const existingPost = client.cache.readFragment({
    id: normalizedId,
    fragment: graphql(`
      fragment Post_evicting on Post {
        id
      }
    `),
  })

  if (!existingPost) {
    return
  }

  client.cache.evict({ id: normalizedId })
}

/**
 * Updates a post's data in the Apollo cache
 */
function updateCachedPost(
  id: string,
  data: GetCalendarPostForRealTimeUpdateData | undefined,
): void {
  client.cache.writeQuery({
    query: GetCalendarPostForRealTimeUpdate,
    variables: {
      postId: id,
    },
    data,
  })
}
